import { useState, useEffect } from "react";

interface MaterialityData {
  materiality?: number;
  performanceMateriality?: number;
  clearlyTrivialThreshold?: number;
}

export function usePlanningMateriality() {
  const [materialityData, setMaterialityData] = useState<MaterialityData>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMaterialityData = async () => {
      setLoading(true);
      try {
        // Get company name
        const getCompanyName = async () => {
          try {
            if (window.sharePointAPI?.getCompanyName) {
              const result = await window.sharePointAPI.getCompanyName("combined");
              return result?.data || "ABC Private Limited";
            }
            return "ABC Private Limited";
          } catch {
            return "ABC Private Limited";
          }
        };

        const companyName = await getCompanyName();

        // Load responses from Planning_UserResponses.json
        const planningAPI = (window as any).planningAPI;
        if (!planningAPI?.loadResponses) {
          console.warn("Planning API not available");
          setMaterialityData({});
          setLoading(false);
          return;
        }

        const sharePointResponses = await planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality",
        });

        if (!sharePointResponses || !sharePointResponses.success || !sharePointResponses.data) {
          console.warn("Failed to load planning responses from SharePoint");
          setMaterialityData({});
          setLoading(false);
          return;
        }

        const responses = sharePointResponses.data.responses || sharePointResponses.data;

        // Check M-9 subAnswer to determine if revised materiality should be used
        const m9Response = responses["M-9"];
        const useRevisedMateriality = m9Response?.subAnswer === "Yes";

        let materiality = 0;
        let performanceMateriality = 0;
        let clearlyTrivialThreshold = 0;

        if (useRevisedMateriality) {
          // Use revised materiality values from revisedMateriality question
          const revisedMaterialityResponse = responses["revisedMateriality"];
          if (revisedMaterialityResponse) {
            materiality = parseFloat(revisedMaterialityResponse.determinedMateriality || "0") || 0;
            performanceMateriality = parseFloat(revisedMaterialityResponse.performanceMateriality || "0") || 0;
            clearlyTrivialThreshold = parseFloat(revisedMaterialityResponse.trivialThreshold || "0") || 0;
          }
        } else {
          // Use default values from M-3, M-5, M-8
          // M-3: Materiality from textResponse
          const m3Response = responses["M-3"];
          if (m3Response?.textResponse) {
            const textValue = typeof m3Response.textResponse === "string"
              ? m3Response.textResponse
              : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
            materiality = parseFloat(textValue) || 0;
          }

          // M-5: Performance Materiality from overridePerformanceMateriality
          const m5Response = responses["M-5"];
          if (m5Response?.overridePerformanceMateriality !== undefined) {
            performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality.toString()) || 0;
          }

          // M-8: Clearly Trivial Threshold from textResponse
          const m8Response = responses["M-8"];
          if (m8Response?.textResponse) {
            const textValue = typeof m8Response.textResponse === "string"
              ? m8Response.textResponse
              : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
            clearlyTrivialThreshold = parseFloat(textValue) || 0;
          }
        }

        setMaterialityData({
          materiality,
          performanceMateriality,
          clearlyTrivialThreshold,
        });
      } catch (error) {
        console.error("Failed to load materiality data:", error);
        setMaterialityData({});
      } finally {
        setLoading(false);
      }
    };

    loadMaterialityData();
  }, []);

  return { materialityData, loading };
}

